import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  rowToClinic,
  setLiveClinic,
  type ClinicSettingsRow,
} from "@/lib/clinicLive";

/**
 * Loads the editable clinic settings row from Lovable Cloud once on
 * mount and subscribes to realtime updates. Whenever an admin saves
 * the settings panel, every consumer of `useLiveClinic()` (header,
 * footer, JSON-LD, location SEO) automatically re-renders with the
 * new values — no redeploy required.
 */
const ClinicSettingsBootstrap = () => {
  useEffect(() => {
    let cancelled = false;

    const apply = (row: ClinicSettingsRow | null) => {
      if (!row || cancelled) return;
      setLiveClinic(rowToClinic(row));
    };

    supabase
      .from("clinic_settings")
      .select("*")
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          console.warn("[clinic-settings] fetch failed; using defaults", error.message);
          return;
        }
        apply(data as unknown as ClinicSettingsRow | null);
      });

    // Realtime is only useful for admins editing settings live. Skip it for
    // anonymous public visitors so we don't surface WebSocket connection
    // errors in the console (and burn a connection) on every page view.
    let channel: ReturnType<typeof supabase.channel> | null = null;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled || !data.session) return;
      channel = supabase
        .channel("clinic_settings_live")
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "clinic_settings" },
          (payload) => {
            const next = (payload.new ?? null) as ClinicSettingsRow | null;
            apply(next);
          },
        )
        .subscribe();
    });

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  return null;
};

export default ClinicSettingsBootstrap;
